import React, { useCallback, useEffect, useState } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { clone, equals, head, pickBy } from "ramda";
import { anyTrue, isBlank, notBlank } from "../../../util/ext";
import { useDispatch } from "react-redux";

import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";

import EditThumbnailOverlay from "../../common/EditThumbnailOverlay";
import SnackAlert from "../../../components/SnackAlert";
import { useSnackAlert } from "../../../common/use-snackAlert";
import {
  useModuleAdmin,
  useModuleUpdate,
  useUploadModuleThumbnail,
} from "../moduleServices";
import { useIsAdmin, useIsCreatorManager } from "../../../auth/authUtils";
import {
  firstError,
  hasError,
  validateModule,
  validateModuleField,
} from "../moduleValidation";
import { MediaContext } from "../../common/media-context";
import { useConfirm } from "../../../common/use-confirm";
import { useProjectLink } from "../../creatorServices";
import { Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/pro-regular-svg-icons";

import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CloseIcon from "@material-ui/icons/Close";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiTab from "@material-ui/core/Tab";
import TabContext from "@material-ui/lab/TabContext";
import Tabs from "@material-ui/core/Tabs";
import MuiTabPanel from "@material-ui/lab/TabPanel";
import Skeleton from "@material-ui/lab/Skeleton";
import Typography from "@material-ui/core/Typography";

import ConfirmDialog from "../../../components/ConfirmDialog";
import EditDescriptionColumn from "../../common/EditDescriptionColumn";
import EditModuleInfoColumn from "./EditModuleInfoColumn";
import ModuleFilesTab from "./ModuleFilesTab";
import ModuleSubscriptionsCard from "./ModuleSubscriptionsCard";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import ProjectLink from "../../common/ProjectLink";
import PublishStatusSwitch from "./PublishStatusSwitch";
import ReloadModule from "./ReloadModule";
import SaveButton from "../../../components/SaveButton";
import { useMediaPermissions } from "../../../util/mediaUtil";

const styles = theme => ({
  root: {
    padding: "40px 0 0 0",
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },

  description: {
    marginTop: 45,
    width: "100%",
  },

  dialogRoot: {
    padding: "0 50px",
  },

  actions: {
    paddingTop: 20,
    paddingBottom: 70,
  },

  header: {
    margin: 0,
    padding: "40px 0 20px 0",
  },

  closeButton: {
    color: theme.palette.primary.darker,
    fontWeight: "bolder",
  },

  closeButtonIcon: {
    fontSize: "3.2rem",
  },

  loadingContainer: {
    padding: theme.spacing(2, 5),
    height: "calc(100vh - 200px)",
  },

  scrollArea: {
    height: "calc(100vh - 300px)",
    overflowY: "auto",
    overflowX: "hidden",
  },

  editPanel: {
    height: "100%",
  },

  indicator: {
    height: 0,
  },

  save: {
    width: "140px",
    height: "40px",
    float: "right",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      opacity: "0.7",
    },
  },

  saveTooltip: {
    backgroundColor: "transparent",
    fontStyle: "italic",
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "18px",
    color: "#009D52",
  },

  tabs: {
    minHeight: 80,
    paddingTop: 16,
    paddingBottom: 16,
  },

  tabsFlexContainer: {
    gap: 20,
  },

  tab: {
    minWidth: "auto",
    paddingRight: 0,
    paddingLeft: 0,
  },

  tabPanel: {
    borderTop: "none",
  },
});

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)(props => {
  const { children, classes, onClose, module, ...other } = props;
  const isAdmin = useIsAdmin();
  return (
    <MuiDialogTitle disableTypography className={classes.header} {...other}>
      <Grid container justify="space-between" alignItems="center">
        <Grid container alignItems="center" item xs>
          <Box width={150}>
            <Typography variant="h6">{children}</Typography>
          </Box>
          <Box width={"auto"}>
            <PublishStatusSwitch view="header" module={module} />
          </Box>
          {isAdmin && (
            <Box ml="35px">
              <ReloadModule id={module.id} />
            </Box>
          )}
        </Grid>

        <Grid item container justify="flex-end" alignItems="center" xs>
          {isAdmin && notBlank(module.id) && (
            <Grid item>
              <Button
                component={RouterLink}
                to={`/creator/manage-access/module/${module.id}`}
                startIcon={<FontAwesomeIcon icon={faKey} />}
              >
                Manage Access
              </Button>
            </Grid>
          )}
          <Grid item>
            {onClose ? (
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={onClose}
              >
                <CloseIcon classes={{ root: classes.closeButtonIcon }} />
              </IconButton>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </MuiDialogTitle>
  );
});

const CspProjectLink = ({ id, showAlert }) => {
  const projectLinkResults = useProjectLink(id);

  return projectLinkResults.isLoading ? (
    <Skeleton />
  ) : (
    <ProjectLink
      id={id}
      showAlert={showAlert}
      projectLink={head(projectLinkResults.data)}
    />
  );
};

const Tab = props => {
  const classes = useStyles();
  return <MuiTab {...props} classes={{ root: classes.tab }} />;
};

const TabPanel = props => {
  const classes = useStyles();
  return <MuiTabPanel {...props} classes={{ root: classes.tabPanel }} />;
};

const objectDiff = media => (val, key) => !equals(media[key], val);

const initialModuleState = {
  description: "",
  displayName: "",
  defaultCurationName: "",
  excludeFromCatalog: false,
  iconUrl: "",
  internalCode: "",
  name: "",
  status: "ACTIVE",
  inDplaCatalog: false,
};

const ModuleAdminEditDialog = props => {
  const { visible, id, onCancel } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const isCreatorManager = useIsCreatorManager();

  const [tab, setTab] = useState("metadata");
  const handleTabChange = (_event, newTab) => setTab(newTab);

  //-- Manage the alert message
  // const { showAlert, SnackAlert } = useSnackAlert();
  const snackAlertHandler = useSnackAlert();
  const { showAlert } = snackAlertHandler;

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  //-- Set up the thumbnail uploader
  const thumbnailUpload = useUploadModuleThumbnail();

  //-- Set up overall state
  const [module, setModule] = useState({});
  const [initialModule, setInitialModule] = useState(initialModuleState);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const { readOnly } = useMediaPermissions(module);

  const result = useModuleAdmin(id);
  useEffect(() => {
    if (result.isSuccess) {
      setModule(result.data);
      setInitialModule(clone(result.data));
      setThumbnailUrl(result.data.thumbnailUrl + "&tc=" + Date.now());
      setLoading(false);
    }
  }, [dispatch, result.isSuccess, result.data, result.isError, result.error]);

  //-- Handle saving the content
  const moduleUpdate = useModuleUpdate();
  const handleSave = useCallback(async () => {
    const validation = validateModule(module);
    try {
      if (notBlank(validation)) {
        setErrors(validation);
        throw new Error("Please address missing or invalid values");
      }
      const update = pickBy(objectDiff(initialModule), module);

      await moduleUpdate.mutateAsync({
        data: update,
        resourceIds: {
          id: initialModule.id,
        },
      });

      setInitialModule(module);
      showAlert("success", ["Module Saved"]);
    } catch (err) {
      showAlert("error", [err.message]);
    }
  }, [initialModule, module, moduleUpdate, showAlert]);

  const handleCloseClicked = useCallback(() => {
    const validation = validateModule(module);
    try {
      if (notBlank(validation)) {
        setErrors(validation);
        throw new Error("Please address missing or invalid values");
      }
      const update = pickBy(objectDiff(initialModule), module);

      if (isBlank(update)) {
        onCancel();
      } else {
        showConfirm(
          "Discard Changes?",
          "You have made changes to this module without saving.  Do you wish to discard these changes and go back to the module list?",
          {
            onConfirm: () => {
              onCancel();
            },
          },
        );
      }
    } catch (err) {
      showAlert("error", [err.message]);
    }
  }, [module, initialModule, showAlert, showConfirm, onCancel]);

  const [saveDisabled, setSaveDisabled] = useState(true);
  useEffect(() => {
    const update = pickBy(objectDiff(initialModule), module);
    if (notBlank(update)) {
      setSaveDisabled(false);
    }
  }, [module, initialModule]);

  return (
    <Dialog
      fullScreen
      classes={{ paper: classes.dialogRoot }}
      open={visible}
      onClose={handleCloseClicked}
    >
      {loading ? (
        <div className={classes.loadingContainer}>
          <PanelLoadingIndicator />
        </div>
      ) : result.isError ? (
        <div className={classes.loadingContainer}>
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            An error occurred loading this module:{" "}
            <strong>{result.error.message}</strong>
          </Alert>
          <p></p>
        </div>
      ) : (
        <MediaContext.Provider
          value={{
            initial: initialModule,
            media: module,
            setMedia: setModule,
            errors: errors,
            setErrors: setErrors,
            thumbnailUrl: thumbnailUrl,
            setThumbnailUrl: setThumbnailUrl,
          }}
        >
          <TabContext value={tab}>
            <DialogTitle
              id="edit-module-dialog-title"
              onClose={handleCloseClicked}
              module={module}
            >
              Edit Module
            </DialogTitle>

            <Divider />

            <Tabs
              aria-label="tabs for navigating between module metadata and module files"
              textColor="primary"
              onChange={handleTabChange}
              classes={{
                root: classes.tabs,
                indicator: classes.indicator,
                flexContainer: classes.tabsFlexContainer,
              }}
              value={tab}
            >
              <Tab disableRipple value="metadata" label="Metadata" />
              <Tab disableRipple value="files" label="All Files" />
              {isAdmin && (
                <Tab
                  disableRipple
                  value="subscriptions"
                  label="Status & Subscriptions"
                />
              )}
            </Tabs>

            <Divider />

            <TabPanel value="metadata">
              <DialogContent id="module-edit" classes={{ root: classes.root }}>
                <SnackAlert handler={snackAlertHandler} />
                <ConfirmDialog handler={confirmHandler} />

                <Container
                  id="module-edit-container"
                  maxWidth={false}
                  disableGutters
                >
                  <div className={classes.scrollArea}>
                    <Grid
                      container
                      id="module-edit-panel"
                      className={classes.editPanel}
                    >
                      <Grid container item xs>
                        <Grid item>
                          <EditThumbnailOverlay
                            accept="jpg, jpeg, png, image/jpeg, image/jpg, image/png"
                            showAlert={showAlert}
                            uploader={thumbnailUpload}
                          />
                        </Grid>

                        <Grid item xs>
                          <EditModuleInfoColumn />
                        </Grid>
                      </Grid>

                      <Grid item container>
                        <Grid item xs lg={8} style={{ paddingRight: "10px" }}>
                          <EditDescriptionColumn
                            className={classes.description}
                            enableLinks
                            allowHtml
                            error={hasError(errors, "description")}
                            helperText={firstError(errors, "description")}
                            onBlur={() =>
                              setErrors(
                                validateModuleField(
                                  "description",
                                  module.description,
                                ),
                              )
                            }
                          />
                        </Grid>

                        {anyTrue([isAdmin, isCreatorManager]) && !readOnly && (
                          <Grid item xs lg={4} style={{ paddingTop: 15 }}>
                            <CspProjectLink id={id} showAlert={showAlert} />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  </div>
                </Container>
              </DialogContent>

              <Divider />

              <DialogActions classes={{ root: classes.actions }}>
                <Button onClick={handleCloseClicked}>Cancel</Button>
                <SaveButton
                  loading={moduleUpdate.isLoading}
                  disabled={saveDisabled || readOnly}
                  onClick={handleSave}
                />
              </DialogActions>
            </TabPanel>

            <TabPanel value="files">
              <ModuleFilesTab module={module} />
            </TabPanel>
            {isAdmin && (
              <TabPanel value="subscriptions">
                <Grid item xs>
                  <ModuleSubscriptionsCard module={module} />
                </Grid>
              </TabPanel>
            )}
          </TabContext>
        </MediaContext.Provider>
      )}
    </Dialog>
  );
};

export default ModuleAdminEditDialog;
